import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendCollectionToAnalytics } from "@/utils/analytics";
import { requestCloseSlideOver } from "@/redux/actions/slide_over";
import { borrowerNeedsListService } from "@/redux/services/borrowerNeedsListService";
import { borrowerDashboardService } from "@/redux/services/borrowerDashboard";
import { borrowerNeedsListFileService } from "@/redux/services/borrowerNeedsListFilesService";
import { baseUrl, onPrepareHeadersBlob } from "@/redux/services/helpers";
import { showNotification } from "@/redux/actions/notification";

export const fileProcessorService = createApi({
  reducerPath: "fileProcessorApi",
  tagTypes: ["BorrowerNeedsListFile", "ContactFile", "ContactFileFromLenderDashboard"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeadersBlob,
  }),
  endpoints: builder => ({
    addBorrowerNeedsListFile: builder.mutation({
      query: ({ loanId, borrowerId, needListId, ...body }) => ({
        url: `loans/${loanId}/borrowers/${borrowerId}/needs-list/${needListId}/files`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      }),
      invalidatesTags: [{ type: "BorrowerNeedsListFile", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_DOCUMENT_FILE_CREATED, data.files);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File(s) added to the borrower needs list!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseSlideOver());
          dispatch(borrowerNeedsListService.util.invalidateTags(["BorrowerNeedsList"]));
          dispatch(borrowerNeedsListFileService.util.invalidateTags(["BorrowerNeedsListFile"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    addBorrowerNeedsListFileFromDashboard: builder.mutation({
      query: ({ token, needListId, uploadIds }) => ({
        url: `borrowers/${token}/needs-list/${needListId}/files`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { uploadIds },
      }),
      invalidatesTags: [{ type: "BorrowerNeedsListFile", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_DOCUMENT_FILE_CREATED_BY_BORROWER, data.files);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File(s) added to the borrower needs list!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseSlideOver());
          dispatch(borrowerDashboardService.util.invalidateTags([{ type: "BorrowerNeedsList", id: "LIST" }]));
          dispatch(borrowerDashboardService.util.invalidateTags([{ type: "BorrowerNeedsListFile", id: "LIST" }]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useAddBorrowerNeedsListFileMutation,
  useAddBorrowerNeedsListFileFromDashboardMutation,
} = fileProcessorService;
